/** Cabinet role merging by team size, Head of State placement and rotation. */
import type { CountryId, RoleId, RosterEntry, StudentId, WorldState } from '../shared/types.ts';
import { ROLE_MERGES } from './data/cards.ts';
import { ideo } from './core.ts';

/** Role bundles for a team of `size` students (size 1-6; bigger teams share deputy roles). */
export function roleBundles(size: number): RoleId[][] {
  if (size <= 0) return [];
  const base = ROLE_MERGES[Math.min(size, 6)] ?? ROLE_MERGES[6] ?? [];
  const bundles = base.map((b) => b.slice());
  const deputies: RoleId[] = ['foreign', 'finance', 'interior', 'defense', 'intel'];
  for (let i = 6; i < size; i++) bundles.push([deputies[(i - 6) % deputies.length] as RoleId]);
  return bundles;
}

export interface RoleMember {
  id: StudentId;
  hosVolunteer: boolean;
}

/**
 * Assigns role bundles to members in seat order. The Head of State bundle goes to the first Head of State
 * volunteer if any; everyone else takes the remaining bundles in order.
 */
export function assignRoles(members: RoleMember[]): Map<StudentId, RoleId[]> {
  const out = new Map<StudentId, RoleId[]>();
  const bundles = roleBundles(members.length);
  if (members.length === 0) return out;
  const headIdx = Math.max(0, members.findIndex((m) => m.hosVolunteer));
  const order = [members[headIdx] as RoleMember, ...members.filter((_, i) => i !== headIdx)];
  order.forEach((m, i) => out.set(m.id, (bundles[i] ?? []).slice()));
  return out;
}

export function teamOf(s: WorldState, c: CountryId): RosterEntry[] {
  return s.roster.filter((r) => r.country === c).sort((a, b) => a.seat - b.seat);
}

export function presentTeam(s: WorldState, c: CountryId): RosterEntry[] {
  return teamOf(s, c).filter((r) => r.present);
}

export function headOf(s: WorldState, c: CountryId): RosterEntry | undefined {
  return teamOf(s, c).find((r) => r.roles.includes('head'));
}

/** Re-deals roles for a whole team (used after moves and late joiners). */
export function redealRoles(s: WorldState, c: CountryId): void {
  const team = teamOf(s, c);
  const roles = assignRoles(team.map((r) => ({ id: r.id, hosVolunteer: r.hosVolunteer })));
  for (const r of team) r.roles = roles.get(r.id) ?? [];
}

/**
 * Kestran and Aurem rotate the Head of State role every 5 turns: the next member in seat order takes the
 * head bundle and gives their own bundle to the old head. Returns the new head's id, if it changed.
 */
export function rotateHead(s: WorldState, c: CountryId): StudentId | null {
  if (!ideo(c).rules.headRotates) return null;
  const team = teamOf(s, c);
  if (team.length < 2) return null;
  const hi = team.findIndex((r) => r.roles.includes('head'));
  if (hi < 0) return null;
  const head = team[hi] as RosterEntry;
  const next = team[(hi + 1) % team.length] as RosterEntry;
  const headRoles = head.roles;
  head.roles = next.roles;
  next.roles = headRoles;
  return next.id;
}
